import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain, AlertTriangle, Lightbulb, BookOpen, Activity,
  Play, Sparkles, TrendingDown, TrendingUp, Clock,
  CheckCircle2, XCircle, Eye, ChevronDown, ChevronRight,
  RefreshCw, Zap,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import {
  modelIntelligence,
  type DashboardData, type Alert, type Insight,
  type Research, type AgentRun, type Digest,
  type SavingsSummary,
} from '../services/adminModelIntelligenceService';

// ── Design Tokens ──────────────────────────────────────

const PRIORITY_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  high:     { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  medium:   { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  low:      { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
};

const CONFIDENCE_COLORS: Record<string, { bg: string; text: string }> = {
  high:   { bg: 'bg-green-100', text: 'text-green-800' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-800' },
  low:    { bg: 'bg-gray-100', text: 'text-gray-600' },
};

const VENDOR_COLORS: Record<string, { bg: string; text: string; chart: string }> = {
  openai:     { bg: 'bg-green-100', text: 'text-green-800', chart: '#1F6C4F' },
  anthropic:  { bg: 'bg-violet-100', text: 'text-violet-800', chart: '#8B5CF6' },
  google:     { bg: 'bg-blue-100', text: 'text-blue-800', chart: '#3B82F6' },
  meta:       { bg: 'bg-sky-100', text: 'text-sky-800', chart: '#0EA5E9' },
  xai:        { bg: 'bg-zinc-100', text: 'text-zinc-800', chart: '#71717A' },
  perplexity: { bg: 'bg-teal-100', text: 'text-teal-800', chart: '#14B8A6' },
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  completed: { bg: 'bg-green-100', text: 'text-green-700' },
  running:   { bg: 'bg-blue-100', text: 'text-blue-700' },
  failed:    { bg: 'bg-red-100', text: 'text-red-700' },
  pending:   { bg: 'bg-amber-100', text: 'text-amber-700' },
  active:    { bg: 'bg-green-100', text: 'text-green-700' },
  acknowledged: { bg: 'bg-blue-100', text: 'text-blue-700' },
  resolved:  { bg: 'bg-gray-100', text: 'text-gray-600' },
  dismissed: { bg: 'bg-gray-100', text: 'text-gray-500' },
  profile_created: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  deployed:  { bg: 'bg-green-100', text: 'text-green-700' },
};

const CHART_COLORS = ['#1F6C4F', '#8B5CF6', '#3B82F6', '#0EA5E9', '#71717A', '#14B8A6'];

type Tab = 'dashboard' | 'alerts' | 'insights' | 'research' | 'runs';

// ── Utility Components ─────────────────────────────────

function Badge({ children, color }: { children: React.ReactNode; color: { bg: string; text: string } }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text}`}>
      {children}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return <Badge color={c}>{status.replace(/_/g, ' ')}</Badge>;
}

function VendorBadge({ vendor }: { vendor: string }) {
  const c = VENDOR_COLORS[vendor] || VENDOR_COLORS.openai;
  return <Badge color={c}>{vendor}</Badge>;
}

function MetricCard({ icon: Icon, label, value, sub, accent }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; accent?: string;
}) {
  return (
    <div className="bg-white rounded-xl p-5 hover:shadow-md transition-shadow" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${accent || 'bg-green-50'}`}>
          <Icon className={`w-5 h-5 ${accent ? 'text-white' : 'text-green-700'}`} />
        </div>
        <span className="text-sm font-medium text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function EmptyState({ icon: Icon, title, message }: { icon: React.ElementType; title: string; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="p-4 rounded-full bg-gray-50 mb-4">
        <Icon className="w-10 h-10 text-gray-300" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 max-w-sm">{message}</p>
    </div>
  );
}

function PulsingDot({ color }: { color: string }) {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color} opacity-75`} />
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${color}`} />
    </span>
  );
}

function CostDelta({ value }: { value: number }) {
  if (value === 0) return <span className="text-gray-500 text-sm">No change</span>;
  const isNeg = value < 0;
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-medium ${isNeg ? 'text-green-700' : 'text-red-600'}`}>
      {isNeg ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
      ${Math.abs(value).toFixed(2)}/mo
    </span>
  );
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Main Component ─────────────────────────────────────

export function AdminModelIntelligence() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [digest, setDigest] = useState<Digest | null>(null);
  const [savings, setSavings] = useState<SavingsSummary | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [alertFilter, setAlertFilter] = useState('active');
  const [insights, setInsights] = useState<Insight[]>([]);
  const [insightFilter, setInsightFilter] = useState<string>('');
  const [research, setResearch] = useState<Research[]>([]);
  const [researchVendor, setResearchVendor] = useState('');
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [profileGenerating, setProfileGenerating] = useState(false);
  const [expandedResearch, setExpandedResearch] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [dash, dig, sav] = await Promise.all([
        modelIntelligence.getDashboard(),
        modelIntelligence.getDigest(),
        // Savings is best-effort — backend queries CloudWatch and may take longer
        modelIntelligence.getSavingsSummary(30).catch(() => null),
      ]);
      setDashboard(dash);
      setDigest(dig);
      setSavings(sav);
    } catch (e) {
      console.error('Failed to load dashboard:', e);
      setLoadError('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadAlerts = useCallback(async () => {
    try {
      const data = await modelIntelligence.getAlerts(alertFilter || undefined);
      setAlerts(data.alerts);
    } catch (e) { console.error(e); setLoadError('Failed to load data. Please refresh.'); }
  }, [alertFilter]);

  const loadInsights = useCallback(async () => {
    try {
      const data = await modelIntelligence.getInsights(insightFilter || undefined);
      setInsights(data.insights);
    } catch (e) { console.error(e); setLoadError('Failed to load data. Please refresh.'); }
  }, [insightFilter]);

  const loadResearch = useCallback(async () => {
    try {
      const data = await modelIntelligence.getResearch(researchVendor || undefined);
      setResearch(data.research);
    } catch (e) { console.error(e); setLoadError('Failed to load data. Please refresh.'); }
  }, [researchVendor]);

  const loadRuns = useCallback(async () => {
    try {
      const data = await modelIntelligence.getAgentRuns();
      setRuns(data.runs);
    } catch (e) { console.error(e); setLoadError('Failed to load data. Please refresh.'); }
  }, []);

  useEffect(() => {
    if (activeTab === 'alerts') loadAlerts();
    if (activeTab === 'insights') loadInsights();
    if (activeTab === 'research') loadResearch();
    if (activeTab === 'runs') loadRuns();
  }, [activeTab, loadAlerts, loadInsights, loadResearch, loadRuns]);

  const handleRunPipeline = async () => {
    setPipelineRunning(true);
    try {
      await modelIntelligence.runPipeline();
      await load();
    } catch (e) {
      console.error('Pipeline run failed:', e);
    } finally {
      setPipelineRunning(false);
    }
  };

  const handleGenerateProfile = async () => {
    setProfileGenerating(true);
    try {
      await modelIntelligence.generateProfile();
      await load();
    } catch (e) {
      console.error('Profile generation failed:', e);
    } finally {
      setProfileGenerating(false);
    }
  };

  const handleAlertAction = async (alertId: string, action: string, reason?: string) => {
    try {
      await modelIntelligence.updateAlert(alertId, action, reason);
      loadAlerts();
      load();
    } catch (e) { console.error(e); }
  };

  const tabs: { key: Tab; label: string; icon: React.ElementType; count?: number }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: Brain },
    { key: 'alerts', label: 'Alerts', icon: AlertTriangle, count: digest?.alerts.total_active },
    { key: 'insights', label: 'Insights', icon: Lightbulb, count: digest?.pending_actions.pending_insights },
    { key: 'research', label: 'Research', icon: BookOpen },
    { key: 'runs', label: 'Agent Runs', icon: Activity },
  ];

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  if (loadError && !dashboard) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="text-red-500 text-lg font-medium">{loadError}</div>
        <button onClick={load} className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Header */}
      <div className="rounded-2xl p-8 mb-8 text-white relative overflow-hidden" style={{ background: 'linear-gradient(to right, #113C2B, #1F6C4F, #2A8C65)' }}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA4KSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNnKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-50" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="w-8 h-8" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif" }}>Cedar Model Intelligence</h1>
            {pipelineRunning && <PulsingDot color="bg-white" />}
          </div>
          <p className="text-green-100 text-sm max-w-xl">
            Autonomous LLM landscape research, optimization insights, and A/B test profile generation.
            Researches daily, recommends always, changes never (without your approval).
          </p>
          <div className="flex gap-3 mt-5">
            <button
              onClick={handleRunPipeline}
              disabled={pipelineRunning}
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium px-4 py-2 rounded-lg backdrop-blur-sm transition-all disabled:opacity-50"
            >
              {pipelineRunning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {pipelineRunning ? 'Running...' : 'Run Pipeline'}
            </button>
            <button
              onClick={handleGenerateProfile}
              disabled={profileGenerating}
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium px-4 py-2 rounded-lg backdrop-blur-sm transition-all disabled:opacity-50"
            >
              {profileGenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {profileGenerating ? 'Generating...' : 'Generate Test Profile'}
            </button>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === t.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <DashboardTab dashboard={dashboard} digest={digest} savings={savings} />}
      {activeTab === 'alerts' && (
        <AlertsTab alerts={alerts} filter={alertFilter} setFilter={setAlertFilter} onAction={handleAlertAction} />
      )}
      {activeTab === 'insights' && (
        <InsightsTab insights={insights} filter={insightFilter} setFilter={setInsightFilter} />
      )}
      {activeTab === 'research' && (
        <ResearchTab
          research={research}
          vendor={researchVendor}
          setVendor={setResearchVendor}
          expanded={expandedResearch}
          setExpanded={setExpandedResearch}
        />
      )}
      {activeTab === 'runs' && <RunsTab runs={runs} />}
    </div>
  );
}

// ── Cost Savings Panel ──────────────────────────────────
//
// Quantifies LLM spend avoided by recent optimization work. Renders below the
// metrics row on the dashboard tab. Hidden when total savings = $0 (e.g., no
// CloudWatch data yet or fresh deploy).

const SKIP_LABELS: Record<string, { label: string; description: string }> = {
  early_dedup: {
    label: 'Workout dedup',
    description: 'Skipped re-processing already-ingested workouts before the expensive pipeline ran',
  },
  qia_non_chat: {
    label: 'QIA skipped (non-chat)',
    description: 'Skipped query intent analysis for content extraction / memory consolidation contexts',
  },
  elite_analysis_historical: {
    label: 'Elite analysis (historical)',
    description: 'Skipped LLM coaching insights for historical workout backfills',
  },
  goal_progress_historical: {
    label: 'Goal progress (historical)',
    description: 'Skipped goal progress analysis for workouts that pre-date the user\'s goals',
  },
};

function CostSavingsPanel({ savings }: { savings: SavingsSummary }) {
  const entries = Object.entries(savings.breakdown).filter(
    ([_, v]) => v && v.count > 0
  ) as [string, { count: number; estimated_savings_usd: number; estimated_cost_per_skip_usd: number }][];

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-600">
            <TrendingDown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Cost Savings (Last {savings.period_days} Days)</h3>
            <p className="text-xs text-gray-600 mt-0.5">{savings.calculation_note}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-green-700">${savings.total_estimated_savings_usd.toFixed(2)}</div>
          <div className="text-xs text-gray-600">Total estimated savings</div>
        </div>
      </div>

      {entries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {entries.map(([key, val]) => {
            const meta = SKIP_LABELS[key] ?? { label: key, description: '' };
            return (
              <div key={key} className="bg-white rounded-lg p-3 border border-green-100">
                <div className="text-xs font-medium text-gray-500 mb-1">{meta.label}</div>
                <div className="text-xl font-bold text-gray-900">${val.estimated_savings_usd.toFixed(2)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {val.count.toLocaleString()} skip{val.count !== 1 ? 's' : ''} × ${val.estimated_cost_per_skip_usd.toFixed(4)}
                </div>
                {meta.description && (
                  <div className="text-xs text-gray-400 mt-1 italic">{meta.description}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Dashboard Tab ──────────────────────────────────────

function DashboardTab({
  dashboard,
  digest,
  savings,
}: {
  dashboard: DashboardData | null;
  digest: Digest | null;
  savings: SavingsSummary | null;
}) {
  if (!dashboard || !digest) return null;

  const costTrend = digest.cost_trend;
  const agentStatus = digest.agent_status;

  // Mock 7-day trend data from digest (in production, would come from a dedicated endpoint)
  const trendData = [
    { day: '6d ago', cost: costTrend.week_avg_usd * 0.9 },
    { day: '5d ago', cost: costTrend.week_avg_usd * 1.05 },
    { day: '4d ago', cost: costTrend.week_avg_usd * 0.95 },
    { day: '3d ago', cost: costTrend.week_avg_usd * 1.1 },
    { day: '2d ago', cost: costTrend.week_avg_usd * 0.88 },
    { day: 'Yesterday', cost: costTrend.yesterday_usd },
    { day: 'Today', cost: costTrend.yesterday_usd * 0.6 },
  ];

  const vendorData = dashboard.landscape.map(v => ({
    name: v.vendor,
    value: v.model_count,
    color: VENDOR_COLORS[v.vendor]?.chart || '#71717A',
  }));

  return (
    <div className="space-y-6">
      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={AlertTriangle}
          label="Active Alerts"
          value={digest.alerts.total_active}
          sub={digest.alerts.new_count > 0 ? `${digest.alerts.new_count} new today` : 'All clear'}
        />
        <MetricCard
          icon={Lightbulb}
          label="Pending Insights"
          value={digest.pending_actions.pending_insights}
          sub={`${digest.pending_actions.active_test_profiles} test profile(s) active`}
        />
        <MetricCard
          icon={Zap}
          label="Agent Cost (7d)"
          value={`$${agentStatus.week_agent_cost_usd.toFixed(2)}`}
          sub="Research pipeline"
        />
        <MetricCard
          icon={Activity}
          label="Last Pipeline"
          value={agentStatus.last_run ? timeAgo(agentStatus.last_run.completed_at) : 'Never'}
          sub={agentStatus.last_run?.status === 'completed' ? 'Completed successfully' : agentStatus.last_run?.status || 'Not yet run'}
        />
      </div>

      {/* Cost Savings Panel — quantifies recent optimization work */}
      {savings && savings.total_estimated_savings_usd > 0 && (
        <CostSavingsPanel savings={savings} />
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cost Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">LLM Cost Trend (7 Days)</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Yesterday: ${costTrend.yesterday_usd.toFixed(2)}
                <span className={`ml-2 ${costTrend.delta_percent < 0 ? 'text-green-700' : 'text-red-500'}`}>
                  ({costTrend.delta_percent > 0 ? '+' : ''}{costTrend.delta_percent.toFixed(1)}% vs avg)
                </span>
              </p>
            </div>
            {costTrend.top_call && (
              <div className="text-right">
                <div className="text-xs text-gray-500">Top call</div>
                <div className="text-sm font-medium text-gray-900">{costTrend.top_call.call_name}</div>
              </div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1F6C4F" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1F6C4F" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickFormatter={v => `$${v.toFixed(1)}`} />
              <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`, 'Cost']} />
              <Area type="monotone" dataKey="cost" stroke="#1F6C4F" strokeWidth={2} fill="url(#costGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Model Landscape */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Model Landscape</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={vendorData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={3}>
                {vendorData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v: number, name: string) => [`${v} models`, name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 justify-center mt-2">
            {vendorData.map(v => (
              <span key={v.name} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: v.color }} />
                {v.name} ({v.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Alerts Preview */}
      {dashboard.alerts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Alerts</h3>
          <div className="space-y-3">
            {dashboard.alerts.slice(0, 3).map(alert => (
              <div key={alert.id} className={`flex items-start gap-3 p-3 rounded-lg ${PRIORITY_COLORS[alert.priority]?.bg || 'bg-gray-50'} ${PRIORITY_COLORS[alert.priority]?.border || 'border-gray-200'} border`}>
                <div className="mt-0.5"><PulsingDot color={PRIORITY_COLORS[alert.priority]?.dot || 'bg-gray-500'} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{alert.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{timeAgo(alert.created_at)}</div>
                </div>
                <Badge color={PRIORITY_COLORS[alert.priority] || PRIORITY_COLORS.medium}>
                  {alert.priority}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Alerts Tab ─────────────────────────────────────────

function AlertsTab({ alerts, filter, setFilter, onAction }: {
  alerts: Alert[]; filter: string; setFilter: (f: string) => void;
  onAction: (id: string, action: string, reason?: string) => void;
}) {
  const filters = ['active', 'acknowledged', 'resolved', 'dismissed'];

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {alerts.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="All Clear"
          message={filter === 'active' ? 'No active alerts. The agent is monitoring the landscape.' : `No ${filter} alerts found.`}
        />
      ) : (
        <div className="space-y-4">
          {alerts.map(alert => {
            const pc = PRIORITY_COLORS[alert.priority] || PRIORITY_COLORS.medium;
            return (
              <div key={alert.id} className={`bg-white rounded-xl border ${pc.border} p-5 hover:shadow-md transition-shadow`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="mt-1"><PulsingDot color={pc.dot} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge color={pc}>{alert.priority}</Badge>
                        <Badge color={STATUS_COLORS[alert.alert_type] || STATUS_COLORS.pending}>{alert.alert_type.replace(/_/g, ' ')}</Badge>
                        {alert.confidence && <Badge color={CONFIDENCE_COLORS[alert.confidence] || CONFIDENCE_COLORS.low}>{alert.confidence}</Badge>}
                      </div>
                      <h4 className="text-sm font-semibold text-gray-900">{alert.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                      {alert.affected_calls && alert.affected_calls.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {alert.affected_calls.slice(0, 5).map(c => (
                            <span key={c} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{c}</span>
                          ))}
                          {alert.affected_calls.length > 5 && (
                            <span className="text-xs text-gray-400">+{alert.affected_calls.length - 5} more</span>
                          )}
                        </div>
                      )}
                      {alert.cost_delta_monthly != null && alert.cost_delta_monthly !== 0 && (
                        <div className="mt-2"><CostDelta value={alert.cost_delta_monthly} /></div>
                      )}
                      <div className="text-xs text-gray-400 mt-2">{timeAgo(alert.created_at)}</div>
                    </div>
                  </div>

                  {alert.status === 'active' && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        onClick={() => onAction(alert.id, 'acknowledge')}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" /> Acknowledge
                      </button>
                      <button
                        onClick={() => onAction(alert.id, 'resolve')}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                      </button>
                      <button
                        onClick={() => onAction(alert.id, 'dismiss', 'Dismissed from portal')}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Dismiss
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Insights Tab ───────────────────────────────────────

function InsightsTab({ insights, filter, setFilter }: {
  insights: Insight[]; filter: string; setFilter: (f: string) => void;
}) {
  const filters = ['', 'pending', 'profile_created', 'deployed', 'dismissed'];

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f ? f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'All'}
          </button>
        ))}
      </div>

      {insights.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title="No Insights Yet"
          message="The agent is researching the landscape. Insights will appear when optimization opportunities are found."
        />
      ) : (
        <div className="space-y-4">
          {insights.map(insight => (
            <div key={insight.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <StatusBadge status={insight.status} />
                  <Badge color={CONFIDENCE_COLORS[insight.confidence] || CONFIDENCE_COLORS.low}>
                    {insight.confidence} confidence
                  </Badge>
                  <span className="text-xs text-gray-400">{insight.insight_type.replace(/_/g, ' ')}</span>
                </div>
                <CostDelta value={insight.cost_delta_monthly} />
              </div>

              {insight.research_title && (
                <div className="flex items-center gap-2 mb-2">
                  {insight.vendor && <VendorBadge vendor={insight.vendor} />}
                  <span className="text-sm text-gray-700">{insight.research_title}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-1.5 mb-3">
                {insight.affected_calls.map(c => (
                  <span key={c} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-mono">{c}</span>
                ))}
              </div>

              {insight.quality_assessment && (
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{insight.quality_assessment}</p>
              )}

              {insight.speed_delta_percent !== 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm text-gray-500">
                  <Zap className="w-3.5 h-3.5" />
                  Speed: {insight.speed_delta_percent > 0 ? '+' : ''}{insight.speed_delta_percent}%
                </div>
              )}

              <div className="text-xs text-gray-400 mt-3">{timeAgo(insight.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Research Tab ────────────────────────────────────────

function ResearchTab({ research, vendor, setVendor, expanded, setExpanded }: {
  research: Research[]; vendor: string; setVendor: (v: string) => void;
  expanded: Set<string>; setExpanded: (s: Set<string>) => void;
}) {
  const vendors = ['', 'openai', 'anthropic', 'google', 'meta', 'xai', 'perplexity'];

  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {vendors.map(v => (
          <button
            key={v}
            onClick={() => setVendor(v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              vendor === v ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {v || 'All'}
          </button>
        ))}
      </div>

      {research.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No Research Yet"
          message="The scout agent hasn't detected any landscape changes. Research appears after the daily 4 AM UTC scan."
        />
      ) : (
        <div className="space-y-3">
          {research.map(r => {
            const isExpanded = expanded.has(r.id);
            let findings = '';
            if (r.key_findings) {
              try {
                const kf = typeof r.key_findings === 'string' ? JSON.parse(r.key_findings) : r.key_findings;
                findings = (kf as Record<string, unknown>).deep_research as string || '';
              } catch { findings = ''; }
            }

            return (
              <div key={r.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <button
                  onClick={() => toggleExpand(r.id)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                  <VendorBadge vendor={r.vendor} />
                  <Badge color={STATUS_COLORS[r.research_type] || STATUS_COLORS.pending}>
                    {r.research_type.replace(/_/g, ' ')}
                  </Badge>
                  <span className="text-sm font-medium text-gray-900 flex-1 truncate">{r.title}</span>
                  {r.estimated_impact && (
                    <Badge color={
                      r.estimated_impact === 'high' ? CONFIDENCE_COLORS.high :
                      r.estimated_impact === 'medium' ? CONFIDENCE_COLORS.medium : CONFIDENCE_COLORS.low
                    }>
                      {r.estimated_impact}
                    </Badge>
                  )}
                  <span className="text-xs text-gray-400 shrink-0">{timeAgo(r.created_at)}</span>
                </button>
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                    <div className="text-sm text-gray-600 mb-3">{r.summary}</div>
                    {findings && (
                      <div className="bg-white rounded-lg border border-gray-200 p-4 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                        {findings}
                      </div>
                    )}
                    {r.source_urls && r.source_urls.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs font-medium text-gray-500 mb-1">Sources</div>
                        <div className="flex flex-wrap gap-2">
                          {r.source_urls.filter(Boolean).map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-green-700 hover:text-green-800 hover:underline truncate max-w-xs">
                              {url}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {r.model_name && (
                      <div className="mt-3 text-xs text-gray-500">
                        Model: <span className="font-mono">{r.model_name}</span>
                        {r.processed && <span className="ml-3 text-green-700">Analyzed</span>}
                        {!r.processed && <span className="ml-3 text-amber-600">Pending analysis</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Agent Runs Tab ─────────────────────────────────────

function RunsTab({ runs }: { runs: AgentRun[] }) {
  if (runs.length === 0) {
    return (
      <EmptyState
        icon={Activity}
        title="No Runs Yet"
        message="Agent pipeline hasn't run yet. Click 'Run Pipeline' to start the first scan."
      />
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>When</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>Agent</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>Status</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>Duration</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>Searches</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>Insights</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>Alerts</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: '#F3F3F3', color: '#9CA3AF' }}>Cost</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {runs.map(run => (
            <tr key={run.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 text-sm text-gray-600">{timeAgo(run.started_at)}</td>
              <td className="px-4 py-3">
                <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                  {run.agent_type}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {run.status === 'running' && <PulsingDot color="bg-blue-500" />}
                  <StatusBadge status={run.status} />
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 text-right">
                {run.duration_seconds != null ? `${run.duration_seconds}s` : '—'}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 text-right">{run.searches_performed}</td>
              <td className="px-4 py-3 text-sm text-gray-600 text-right">{run.insights_generated}</td>
              <td className="px-4 py-3 text-sm text-gray-600 text-right">{run.alerts_created}</td>
              <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                ${run.total_cost_usd.toFixed(4)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
